/**
 * "Support access" — every time a WeaveIn super admin viewed the app as you
 * (TRUS-747). Shown to staff in their profile settings and to members in the
 * portal settings, so the notification email always has somewhere to point.
 */
import { useEffect, useState } from 'react';
import { Eye, Loader2, PenLine } from 'lucide-react';
import { authFetch } from '@/lib/authFetch';

interface AccessRow {
  id: number;
  actor_name: string | null;
  cbo_name: string | null;
  mode: 'read_only' | 'read_write';
  ticket_key: string;
  classification_label: string;
  started_at: string;
  ended_at: string | null;
  expires_at: string;
  changes_made: number;
}

interface Props {
  /** Defaults to the staff authFetch; the member portal passes memberApiFetch. */
  fetcher?: (url: string) => Promise<Response>;
}

function when(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function minutesBetween(a: string, b: string): number {
  return Math.max(1, Math.round((Date.parse(b) - Date.parse(a)) / 60000));
}

export function SupportAccessList({ fetcher }: Props) {
  const [rows, setRows] = useState<AccessRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const get = fetcher ?? ((url: string) => authFetch(url));
    get('/api/impersonation/my-access')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => setRows(data.sessions || []))
      .catch(() => setError(true));
  }, [fetcher]);

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        When WeaveIn support opens your account to help with a request, it is listed here. They can never send messages,
        sign, pay or change your password as you, and you get an email each time.
      </p>
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Could not load support access.</p>
      ) : rows === null ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No one from support has accessed your account.</p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {rows.map((r) => (
            <li key={r.id} className="flex items-start gap-3 px-3 py-2 text-sm">
              {r.mode === 'read_write' ? (
                <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              ) : (
                <Eye className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              )}
              <div className="min-w-0">
                <div className="text-gray-900 dark:text-white">
                  {r.actor_name || 'WeaveIn support'} · {when(r.started_at)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {r.mode === 'read_write' ? 'View and change' : 'View only'} · {r.classification_label} · {r.ticket_key}
                  {' · '}
                  {r.cbo_name}
                  {' · '}
                  {r.ended_at ? `${minutesBetween(r.started_at, r.ended_at)} min` : 'in progress'}
                  {r.changes_made > 0 ? ` · ${r.changes_made} change${r.changes_made === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SupportAccessList;
